/**
 * Sistema de Feature Flags para habilitar/deshabilitar características
 * Implementa un patrón observable para notificar cambios a los componentes
 */

// Enum de los feature flags disponibles en la aplicación
export enum FeatureFlags {
  // Características de UI
  USE_NEUMORPHISM = 'USE_NEUMORPHISM',
  ENABLE_DARK_MODE = 'ENABLE_DARK_MODE',
  NEUMORPHISM = 'NEUMORPHISM',

  // Módulos principales
  DASHBOARD = 'DASHBOARD',
  TODO_MODULE = 'TODO_MODULE',
  USERS_MODULE = 'USERS_MODULE',
  SETTINGS_MODULE = 'SETTINGS_MODULE',
  ADMIN_MODULE = 'ADMIN_MODULE',
  REPORTS_MODULE = 'REPORTS_MODULE',
  INVESTMENT_ORDER_MODULE = 'INVESTMENT_ORDER_MODULE',
  LIMIT_CONTROL_MODULE = 'LIMIT_CONTROL_MODULE',
  ORDERS_MODULE = 'ORDERS_MODULE',
  DASHBOARD_MODULE = 'DASHBOARD_MODULE',
  //Agregar nuevo módulo

  // Submódulos y características específicas
  USER_PROFILES = 'USER_PROFILES',
  USER_ROLES = 'USER_ROLES',
  GENERAL_SETTINGS = 'GENERAL_SETTINGS',
  SECURITY_SETTINGS = 'SECURITY_SETTINGS',
  NOTIFICATIONS = 'NOTIFICATIONS',
  ADVANCED_SECURITY = 'ADVANCED_SECURITY',
  INVESTMENT_ORDER_DAY_OPERATIONS = 'INVESTMENT_ORDER_DAY_OPERATIONS',
  INVESTMENT_ORDER_FIXED_INCOME = 'INVESTMENT_ORDER_FIXED_INCOME',
  INVESTMENT_ORDER_VARIABLE_INCOME = 'INVESTMENT_ORDER_VARIABLE_INCOME',
  LIMIT_CONFIG_MODULE = 'LIMIT_CONFIG_MODULE',
  LIMIT_EVALUATION_MODULE = 'LIMIT_EVALUATION_MODULE',
  ORDERS_FIXED_INCOME = 'ORDERS_FIXED_INCOME',
  ORDERS_VARIABLE_INCOME = 'ORDERS_VARIABLE_INCOME',
  ORDERS_INSTRUMENTS = 'ORDERS_INSTRUMENTS',
  ORDERS_LIST = 'ORDERS_LIST',
  CLIENT_ORDERS = 'CLIENT_ORDERS',
  DASHBOARD_GRAPHICS = 'DASHBOARD_GRAPHICS',
  DASHBOARD_FUTURE_FLOWS = 'DASHBOARD_FUTURE_FLOWS',
  DASHBOARD_SENSITIVITY_MEASURES = 'DASHBOARD_SENSITIVITY_MEASURES',
}

// Tipo para el callback de observadores
type FeatureFlagObserver = (flag: FeatureFlags, enabled: boolean) => void;

// Estado predeterminado de los feature flags
const defaultFeatureFlags: Record<FeatureFlags, boolean> = {
  // Características de UI habilitadas por defecto
  [FeatureFlags.USE_NEUMORPHISM]: true,
  [FeatureFlags.ENABLE_DARK_MODE]: true,

  // Módulos principales habilitados por defecto
  [FeatureFlags.DASHBOARD]: true,
  [FeatureFlags.DASHBOARD_MODULE]: true,
  [FeatureFlags.NEUMORPHISM]: true,
  [FeatureFlags.TODO_MODULE]: true,
  [FeatureFlags.USERS_MODULE]: true,
  [FeatureFlags.SETTINGS_MODULE]: true,
  [FeatureFlags.ADMIN_MODULE]: true,
  [FeatureFlags.REPORTS_MODULE]: false, // Reportes desactivado por defecto
  [FeatureFlags.INVESTMENT_ORDER_MODULE]: true,
  [FeatureFlags.LIMIT_CONTROL_MODULE]: true,
  [FeatureFlags.ORDERS_MODULE]: true,
  //Activar nuevo módulo 
  // Submódulos específicos
  [FeatureFlags.USER_PROFILES]: true,
  [FeatureFlags.USER_ROLES]: true,
  [FeatureFlags.GENERAL_SETTINGS]: true,
  [FeatureFlags.SECURITY_SETTINGS]: true,
  [FeatureFlags.NOTIFICATIONS]: true,
  [FeatureFlags.ADVANCED_SECURITY]: false, // Seguridad avanzada desactivada por defecto
  [FeatureFlags.INVESTMENT_ORDER_DAY_OPERATIONS]: true,
  [FeatureFlags.INVESTMENT_ORDER_FIXED_INCOME]: true,
  [FeatureFlags.INVESTMENT_ORDER_VARIABLE_INCOME]: true,
  [FeatureFlags.LIMIT_CONFIG_MODULE]: true,
  [FeatureFlags.LIMIT_EVALUATION_MODULE]: true,
  [FeatureFlags.ORDERS_FIXED_INCOME]: true,
  [FeatureFlags.ORDERS_VARIABLE_INCOME]: true,
  [FeatureFlags.ORDERS_INSTRUMENTS]: true,
  [FeatureFlags.ORDERS_LIST]: true,
  [FeatureFlags.CLIENT_ORDERS]: true,
  [FeatureFlags.DASHBOARD_GRAPHICS]: true,
  [FeatureFlags.DASHBOARD_FUTURE_FLOWS]: true,
  [FeatureFlags.DASHBOARD_SENSITIVITY_MEASURES]: true,
};

// Estructura de datos para el panel de administración de feature flags
export const featureFlags = [
  // UI Features
  {
    name: FeatureFlags.USE_NEUMORPHISM,
    description: 'Enable neumorphic design style throughout the application',
    enabled: defaultFeatureFlags[FeatureFlags.USE_NEUMORPHISM],
    group: 'UI'
  },
  {
    name: FeatureFlags.ENABLE_DARK_MODE,
    description: 'Enable dark mode support',
    enabled: defaultFeatureFlags[FeatureFlags.ENABLE_DARK_MODE],
    group: 'UI'
  },

  // Core Modules
  {
    name: FeatureFlags.DASHBOARD_MODULE,
    description: '',
    enabled: defaultFeatureFlags[FeatureFlags.DASHBOARD_MODULE],
    requiresAuth: true,
    group: 'Core',
    permissions: ["admin"],
    subFeatures: [
      {
        name: FeatureFlags.DASHBOARD_GRAPHICS,
        description: 'Visor de portafolios Graficos',
        enabled: defaultFeatureFlags[FeatureFlags.DASHBOARD_GRAPHICS],
        requiresAuth: true,
        permissions: ['admin']
      },
      {
        name: FeatureFlags.DASHBOARD_FUTURE_FLOWS,
        description: 'Flujos futuros',
        enabled: defaultFeatureFlags[FeatureFlags.DASHBOARD_FUTURE_FLOWS],
        requiresAuth: true,
        permissions: ['admin']
      },
      {
        name: FeatureFlags.DASHBOARD_SENSITIVITY_MEASURES,
        description: 'Medidas de sensibilidad',
        enabled: defaultFeatureFlags[FeatureFlags.DASHBOARD_SENSITIVITY_MEASURES],
        requiresAuth: true,
        permissions: ['admin']
      },
    ]
  },
  {
    name: FeatureFlags.LIMIT_CONTROL_MODULE,
    description: '',
    enabled: defaultFeatureFlags[FeatureFlags.LIMIT_CONTROL_MODULE],
    requiresAuth: true,
    group: 'Core',
    permissions: ["admin"],
    subFeatures: [
      {
        name: FeatureFlags.LIMIT_CONFIG_MODULE,
        description: 'Configuración de los límites',
        enabled: defaultFeatureFlags[FeatureFlags.LIMIT_CONFIG_MODULE],
        requiresAuth: true,
        permissions: ['admin']
      },
      {
        name: FeatureFlags.LIMIT_EVALUATION_MODULE,
        description: 'Evaluación de los límites',
        enabled: defaultFeatureFlags[FeatureFlags.LIMIT_EVALUATION_MODULE],
        requiresAuth: true,
        permissions: ['admin']
      }
    ]
  },
  {
    name: FeatureFlags.ORDERS_MODULE,
    description: 'Registro de ordenes, completar o rechazar',
    enabled: defaultFeatureFlags[FeatureFlags.ORDERS_MODULE],
    requiresAuth: true,
    group: 'Core',
    permissions: ["admin"],
    subFeatures: [
      {
        name: FeatureFlags.ORDERS_FIXED_INCOME,
        description: 'Oredenes por renta fija',
        enabled: defaultFeatureFlags[FeatureFlags.ORDERS_FIXED_INCOME],
        requiresAuth: true,
        permissions: ['admin']
      },
      {
        name: FeatureFlags.ORDERS_VARIABLE_INCOME,
        description: 'Oredenes por renta variable',
        enabled: defaultFeatureFlags[FeatureFlags.ORDERS_VARIABLE_INCOME],
        requiresAuth: true,
        permissions: ['admin']
      },
      {
        name: FeatureFlags.ORDERS_INSTRUMENTS,
        description: 'Manejo de instrumentos',
        enabled: defaultFeatureFlags[FeatureFlags.ORDERS_INSTRUMENTS],
        requiresAuth: true,
        permissions: ['admin']
      },
      {
        name: FeatureFlags.ORDERS_LIST,
        description: 'Lista general de ordenes',
        enabled: defaultFeatureFlags[FeatureFlags.ORDERS_LIST],
        requiresAuth: true,
        permissions: ['admin']
      },
      {
        name: FeatureFlags.CLIENT_ORDERS,
        description: 'Ordenes de clientes',
        enabled: defaultFeatureFlags[FeatureFlags.CLIENT_ORDERS],
        requiresAuth: true,
        permissions: ['admin']
      },
    ]
  },
  // Aquí agregar descripción de nuevo módulo
];

// Clase para gestionar feature flags con patrón observer
class FeatureFlagsManager {
  private flags: Record<FeatureFlags, boolean>;
  private observers: Array<FeatureFlagObserver> = [];

  constructor(initialFlags: Record<FeatureFlags, boolean>) {
    // Inicializamos con los valores predeterminados
    this.flags = { ...initialFlags };

    // Intentamos cargar configuración desde localStorage si existe
    this.loadFromStorage();
  }

  // Verificar si un feature está habilitado
  public isEnabled(flag: FeatureFlags): boolean {
    return this.flags[flag] ?? false;
  }

  // Habilitar un feature específico
  public enable(flag: FeatureFlags): void {
    if (!this.flags[flag]) {
      this.flags[flag] = true;
      this.notifyObservers(flag, true);
      this.saveToStorage();
    }
  }

  // Deshabilitar un feature específico
  public disable(flag: FeatureFlags): void {
    if (this.flags[flag]) {
      this.flags[flag] = false;
      this.notifyObservers(flag, false);
      this.saveToStorage();
    }
  }

  // Cambiar el estado de un feature
  public toggle(flag: FeatureFlags): boolean {
    this.flags[flag] = !this.flags[flag];
    this.notifyObservers(flag, this.flags[flag]);
    this.saveToStorage();
    return this.flags[flag];
  }

  // Actualizar múltiples features a la vez
  public updateFeatures(updates: Partial<Record<FeatureFlags, boolean>>): void {
    Object.entries(updates).forEach(([flag, enabled]) => {
      const featureFlag = flag as FeatureFlags;
      if (this.flags[featureFlag] !== enabled) {
        this.flags[featureFlag] = enabled!;
        this.notifyObservers(featureFlag, enabled!);
      }
    });
    this.saveToStorage();
  }

  // Restablecer a valores predeterminados
  public resetToDefaults(): void {
    Object.entries(defaultFeatureFlags).forEach(([flag, enabled]) => {
      const featureFlag = flag as FeatureFlags;
      if (this.flags[featureFlag] !== enabled) {
        this.flags[featureFlag] = enabled;
        this.notifyObservers(featureFlag, enabled);
      }
    });
    this.saveToStorage();
  }

  // Obtener todos los feature flags actuales
  public getAllFeatures(): Record<FeatureFlags, boolean> {
    return { ...this.flags };
  }

  // Suscribirse a cambios en feature flags
  public subscribe(observer: FeatureFlagObserver): () => void {
    this.observers.push(observer);
    return () => {
      this.observers = this.observers.filter(obs => obs !== observer);
    };
  }

  // Notificar a los observadores sobre un cambio
  private notifyObservers(flag: FeatureFlags, enabled: boolean): void {
    this.observers.forEach(observer => observer(flag, enabled));
  }

  // Guardar configuración en localStorage
  private saveToStorage(): void {
    try {
      localStorage.setItem('featureFlags', JSON.stringify(this.flags));
    } catch (error) {
      console.error('Error al guardar feature flags en localStorage:', error);
    }
  }

  // Cargar configuración desde localStorage
  private loadFromStorage(): void {
    try {
      const storedFlags = localStorage.getItem('featureFlags');
      if (storedFlags) {
        const parsedFlags = JSON.parse(storedFlags);
        // Solo aplicar flags válidos que existan en la enumeración
        Object.keys(parsedFlags).forEach(flag => {
          if (Object.values(FeatureFlags).includes(flag as FeatureFlags)) {
            this.flags[flag as FeatureFlags] = parsedFlags[flag];
          }
        });
      }
    } catch (error) {
      console.error('Error al cargar feature flags desde localStorage:', error);
    }
  }
}

// Instancia singleton del gestor de feature flags
export const featureFlagsManager = new FeatureFlagsManager(defaultFeatureFlags);

// Funciones de conveniencia para facilitar el uso
export const isFeatureEnabled = (flag: FeatureFlags): boolean => {
  return featureFlagsManager.isEnabled(flag);
};

export const enableFeature = (flag: FeatureFlags): void => {
  featureFlagsManager.enable(flag);
};

export const disableFeature = (flag: FeatureFlags): void => {
  featureFlagsManager.disable(flag);
};

export const toggleFeature = (flag: FeatureFlags): boolean => {
  return featureFlagsManager.toggle(flag);
};

export const updateFeatures = (updates: Partial<Record<FeatureFlags, boolean>>): void => {
  featureFlagsManager.updateFeatures(updates);
};

export const resetFeatures = (): void => {
  featureFlagsManager.resetToDefaults();
};

export const getAllFeatures = (): Record<FeatureFlags, boolean> => {
  return featureFlagsManager.getAllFeatures();
};

export const subscribeToFeatureFlags = (
  callback: FeatureFlagObserver
): () => void => {
  return featureFlagsManager.subscribe(callback);
};